/**
 * LearningImpactTab
 * 
 * Catalyst-only tab for viewing training effectiveness and outcomes.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  TrendingDown,
  BookOpen,
  CheckCircle2,
  XCircle,
  Star,
  ThumbsUp,
  Loader2,
  BarChart3
} from 'lucide-react';
import { useEffectivenessEvents, useEffectivenessSummary, useJobOutcomes } from '@/hooks/useTrainingEffectiveness';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
// Batch 18-E13: technician-facing manager-verification visibility.
// Read-only inline query + small Card section + verification badge.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { TrainingVerificationBadge } from '@/components/training/TrainingVerificationBadge';
import { TrainingRevocationBadge } from '@/components/training/TrainingRevocationBadge';
import { ShieldCheck, ShieldOff } from 'lucide-react';

interface LearningImpactTabProps {
  technicianProfileId: string;
}

interface VerifiedSessionRow {
  id: string;
  manager_verified_at: string | null;
  manager_verified_by: string | null;
  manager_verification_note: string | null;
  completion_confidence: string | null;
  completion_status: string | null;
  artifact: {
    id: string;
    title: string;
    provider_name: string | null;
  } | null;
}

// Batch 18-E18: revoked audit row shape. The audit table has no FK
// to training_artifacts (artifact_id is denormalized), so the row
// displays a "Training session" fallback rather than threading a
// deep nested join. A follow-on batch can hydrate artifact titles
// via a batched lookup if user demand justifies it.
interface RevokedVerificationRow {
  id: string;
  session_id: string;
  verification_status: string;
  verification_note: string | null;
  created_at: string;
  artifact_id: string;
  metadata: {
    auto_revoked?: boolean;
    one_shot_reconciliation?: boolean;
    restored_confidence?: string;
    [key: string]: unknown;
  } | null;
}

export function LearningImpactTab({ technicianProfileId }: LearningImpactTabProps) {
  const { data: events, isLoading: loadingEvents } = useEffectivenessEvents(technicianProfileId, { limit: 50 });
  const { data: summary, isLoading: loadingSummary } = useEffectivenessSummary(technicianProfileId);
  const { data: outcomes, isLoading: loadingOutcomes } = useJobOutcomes(technicianProfileId, { limit: 20 });

  // Batch 18-E13: read-only inline query for manager-verified sessions.
  // Gated on the same managerTrainingVerification feature flag as the
  // 18-E12 manager-side surface so Passport visibility stays in lockstep
  // with the staged rollout. Skips network entirely when disabled.
  const verificationFlagEnabled = useFeatureFlag('managerTrainingVerification');
  const { data: verifiedSessions } = useQuery({
    queryKey: ['passport-verified-sessions', technicianProfileId],
    queryFn: async (): Promise<VerifiedSessionRow[]> => {
      if (!technicianProfileId) return [];
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(
          'id, manager_verified_at, manager_verified_by, manager_verification_note, completion_confidence, completion_status, artifact:training_artifacts(id, title, provider_name)',
        )
        .eq('technician_profile_id', technicianProfileId)
        .not('manager_verified_at', 'is', null)
        .order('manager_verified_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as unknown as VerifiedSessionRow[];
    },
    enabled: !!technicianProfileId && verificationFlagEnabled,
  });

  // Batch 18-E18: read-only inline query for the technician's own
  // revoked verification audit rows. 18-E9 RLS policy
  // "Technicians can view own verification rows" gates access; the
  // explicit technician_profile_id filter is belt-and-braces. Gated
  // on the same managerTrainingVerification flag — when disabled,
  // no network traffic.
  const { data: revokedRows } = useQuery({
    queryKey: ['passport-revoked-verifications', technicianProfileId],
    queryFn: async (): Promise<RevokedVerificationRow[]> => {
      if (!technicianProfileId) return [];
      // Batch 18-F1: training_completion_verifications is now present
      // in the regenerated src/integrations/supabase/types.ts. The
      // 18-E18 local cast that worked around TS2589 has been removed;
      // the typed supabase client handles this query natively.
      const { data, error } = await supabase
        .from('training_completion_verifications')
        .select(
          'id, session_id, verification_status, verification_note, created_at, artifact_id, metadata',
        )
        .eq('technician_profile_id', technicianProfileId)
        .eq('verification_status', 'revoked')
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as unknown as RevokedVerificationRow[];
    },
    enabled: !!technicianProfileId && verificationFlagEnabled,
  });

  // Batch 18-E19a: hydrate artifact title + provider for each revoked
  // row. The 18-E18 audit-table query selects artifact_id; this
  // second query batches the artifact lookup by id IN (...).
  // training_artifacts IS in the generated Supabase types (the 18-E13
  // verified-sessions query joins through it), so no local cast is
  // needed. The hydration is OPTIONAL — when the lookup fails, the
  // row falls back to the 18-E18 "Training session" placeholder.
  const revokedArtifactIds = useMemo(() => {
    if (!revokedRows) return [] as string[];
    const ids = new Set<string>();
    for (const row of revokedRows) {
      if (row.artifact_id) ids.add(row.artifact_id);
    }
    return Array.from(ids);
  }, [revokedRows]);

  const { data: revokedArtifactRows } = useQuery({
    queryKey: [
      'passport-revoked-verification-artifacts',
      technicianProfileId,
      revokedArtifactIds,
    ],
    queryFn: async (): Promise<
      Array<{ id: string; title: string; provider_name: string | null }>
    > => {
      if (revokedArtifactIds.length === 0) return [];
      const { data, error } = await supabase
        .from('training_artifacts')
        .select('id, title, provider_name')
        .in('id', revokedArtifactIds);
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        title: string;
        provider_name: string | null;
      }>;
    },
    enabled:
      verificationFlagEnabled &&
      !!revokedRows &&
      revokedArtifactIds.length > 0,
  });

  const revokedArtifactById = useMemo(() => {
    const map = new Map<
      string,
      { id: string; title: string; provider_name: string | null }
    >();
    for (const a of revokedArtifactRows ?? []) {
      map.set(a.id, a);
    }
    return map;
  }, [revokedArtifactRows]);

  const isLoading = loadingEvents || loadingSummary || loadingOutcomes;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasData = events && events.length > 0;

  if (!hasData) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="font-medium">No training data yet</p>
        <p className="text-sm mt-1">Complete some training to see your impact metrics</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{summary?.totalCompleted || 0}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{Math.round(summary?.completionRate || 0)}%</p>
                <p className="text-xs text-muted-foreground">Completion Rate</p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">
                  {summary?.averageRating ? summary.averageRating.toFixed(1) : '-'}
                </p>
                <p className="text-xs text-muted-foreground">Avg Rating</p>
              </div>
              <Star className="h-8 w-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">
                  {summary?.averageConfidenceDelta !== null 
                    ? (summary.averageConfidenceDelta > 0 ? '+' : '') + summary.averageConfidenceDelta.toFixed(1)
                    : '-'
                  }
                </p>
                <p className="text-xs text-muted-foreground">Confidence Δ</p>
              </div>
              <ThumbsUp className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Completion Progress */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Training Engagement</CardTitle>
          <CardDescription className="text-xs">
            How you interact with training resources
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Opened</span>
              <span className="text-muted-foreground">{summary?.totalOpened || 0}</span>
            </div>
            <Progress value={100} className="h-2" />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Completed</span>
              <span className="text-muted-foreground">{summary?.totalCompleted || 0}</span>
            </div>
            <Progress 
              value={(summary?.totalOpened || 0) > 0 
                ? ((summary?.totalCompleted || 0) / (summary?.totalOpened || 1)) * 100 
                : 0
              } 
              className="h-2" 
            />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Abandoned</span>
              <span className="text-muted-foreground">{summary?.totalAbandoned || 0}</span>
            </div>
            <Progress 
              value={(summary?.totalOpened || 0) > 0 
                ? ((summary?.totalAbandoned || 0) / (summary?.totalOpened || 1)) * 100 
                : 0
              } 
              className="h-2 [&>div]:bg-destructive" 
            />
          </div>
        </CardContent>
      </Card>

      {/* Job Outcomes Correlation */}
      {outcomes && outcomes.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Training → Job Performance
            </CardTitle>
            <CardDescription className="text-xs">
              How training impacts your work
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {outcomes.slice(0, 5).map((outcome) => {
                const metrics = outcome.outcome_metrics as {
                  variance_minutes?: number;
                  comeback_flag?: boolean;
                  completion_success?: boolean;
                };
                const varianceMinutes = metrics?.variance_minutes;
                const isPositive = varianceMinutes !== undefined && varianceMinutes < 0;
                
                return (
                  <div 
                    key={outcome.id}
                    className="flex items-center justify-between p-2 rounded-lg border bg-muted/30"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {outcome.domain_key?.replace(/_/g, ' ') || 'General'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(outcome.created_at), 'MMM d')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {metrics?.completion_success !== undefined && (
                        metrics.completion_success 
                          ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                          : <XCircle className="h-4 w-4 text-destructive" />
                      )}
                      {varianceMinutes !== undefined && (
                        <Badge 
                          variant={isPositive ? 'default' : 'secondary'}
                          className={cn("text-xs", isPositive && "bg-green-600")}
                        >
                          {isPositive ? (
                            <TrendingUp className="h-3 w-3 mr-1" />
                          ) : (
                            <TrendingDown className="h-3 w-3 mr-1" />
                          )}
                          {Math.abs(varianceMinutes)} min
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Batch 18-E13: Manager-Verified Training (read-only badges).
          Gated on managerTrainingVerification — hidden entirely
          while the flag is off OR there are no verified sessions. */}
      {verificationFlagEnabled && verifiedSessions && verifiedSessions.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              Manager-Verified Training
            </CardTitle>
            <CardDescription className="text-xs">
              Training your manager has confirmed as complete.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {verifiedSessions.map((row) => (
                <div
                  key={row.id}
                  className="flex items-start justify-between gap-3 p-2 rounded border bg-muted/20"
                  data-testid="passport-verified-row"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {row.artifact?.title ?? 'Training session'}
                    </p>
                    {row.artifact?.provider_name && (
                      <p className="text-xs text-muted-foreground">
                        {row.artifact.provider_name}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    <TrainingVerificationBadge
                      managerVerifiedAt={row.manager_verified_at}
                      managerVerifiedBy={row.manager_verified_by}
                      managerVerificationNote={row.manager_verification_note}
                      completionConfidence={row.completion_confidence}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Batch 18-E18: Verification Updates (read-only revoked-history).
          Surfaces manager-revoked, auto-revoked, and reconciliation-
          repaired audit rows so the technician knows why a previously-
          visible Manager-Verified Training badge disappeared. Gated on
          the same managerTrainingVerification flag and hidden entirely
          when the technician has no revoked rows. */}
      {verificationFlagEnabled && revokedRows && revokedRows.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldOff className="h-4 w-4 text-muted-foreground" />
              Verification Updates
            </CardTitle>
            <CardDescription className="text-xs">
              Training verifications that were retracted.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {revokedRows.map((row) => {
                // Batch 18-E19a: hydrate from the batched
                // training_artifacts lookup. Falls back to the 18-E18
                // "Training session" placeholder when the artifact is
                // missing from the lookup (artifact deleted, lookup
                // failed, etc.).
                const artifact = revokedArtifactById.get(row.artifact_id);
                return (
                <div
                  key={row.id}
                  className="flex items-start justify-between gap-3 p-2 rounded border bg-muted/10"
                  data-testid="passport-revoked-row"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {artifact?.title ?? 'Training session'}
                    </p>
                    {artifact?.provider_name && (
                      <p className="text-xs text-muted-foreground">
                        {artifact.provider_name}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    <TrainingRevocationBadge
                      revokedAt={row.created_at}
                      revocationNote={row.verification_note}
                      autoRevoked={row.metadata?.auto_revoked === true}
                      oneShotReconciliation={
                        row.metadata?.one_shot_reconciliation === true
                      }
                      restoredConfidence={
                        typeof row.metadata?.restored_confidence === 'string'
                          ? row.metadata.restored_confidence
                          : null
                      }
                    />
                  </div>
                </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {events?.slice(0, 10).map((event) => (
              <div 
                key={event.id}
                className="flex items-center justify-between p-2 rounded border bg-muted/20"
              >
                <div className="flex items-center gap-2">
                  <Badge 
                    variant={
                      event.event_type === 'completed' ? 'default' :
                      event.event_type === 'abandoned' ? 'destructive' :
                      'secondary'
                    }
                    className="text-xs"
                  >
                    {event.event_type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(event.created_at), 'MMM d, h:mm a')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {event.rating && (
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-yellow-500" />
                      <span className="text-xs">{event.rating}</span>
                    </div>
                  )}
                  {event.confidence_delta !== null && event.confidence_delta !== undefined && (
                    <Badge 
                      variant={event.confidence_delta > 0 ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {event.confidence_delta > 0 ? '+' : ''}{event.confidence_delta}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
